import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Upload, Image, FileText, Video, Instagram, Globe, ArrowLeft, Send, Hash, Bookmark, Loader2
} from 'lucide-react';
import HashtagSuggest from '@/components/social/HashtagSuggest';
import ImageGenerator from '@/components/social/ImageGenerator';

const API = '/api';

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  { id: 'facebook', label: 'Facebook', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { id: 'linkedin', label: 'LinkedIn', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  { id: 'twitter', label: 'Twitter / X', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  { id: 'website', label: 'Website', color: 'bg-violet-100 text-violet-700 border-violet-200' },
];

export default function ContentUpload() {
  const navigate = useNavigate();
  const [contentType, setContentType] = useState('image');
  const [platforms, setPlatforms] = useState([]);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [purpose, setPurpose] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [blogTitle, setBlogTitle] = useState('');
  const [blogContent, setBlogContent] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    axios.get(`${API}/social/templates`, { withCredentials: true })
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => {});
  }, []);

  const togglePlatform = (id) => {
    setPlatforms(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else if (f.type.startsWith('video/')) {
      setPreview(URL.createObjectURL(f));
    }
  };

  const applyTemplate = (templateId) => {
    const tmpl = templates.find(t => t.template_id === templateId);
    if (!tmpl) return;
    setSelectedTemplate(templateId);
    if (tmpl.template_type === 'hashtag') {
      setHashtags(prev => prev ? `${prev} ${tmpl.content}` : tmpl.content);
    } else if (tmpl.template_type === 'caption') {
      setCaption(prev => prev ? `${prev}\n${tmpl.content}` : tmpl.content);
    } else if (tmpl.template_type === 'blog') {
      setBlogContent(prev => prev ? `${prev}\n${tmpl.content}` : tmpl.content);
    }
    toast.success(`Template "${tmpl.name}" applied`);
  };

  const handleSubmit = async () => {
    if (platforms.length === 0) {
      toast.error('Select at least one platform');
      return;
    }
    if (contentType !== 'blog' && !file) {
      toast.error('Upload a file');
      return;
    }
    if (contentType === 'blog' && !blogContent.trim()) {
      toast.error('Blog content is required');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      if (file) formData.append('file', file);
      formData.append('content_type', contentType);
      formData.append('platforms', JSON.stringify(platforms));
      formData.append('caption', caption);
      formData.append('hashtags', hashtags);
      formData.append('purpose', purpose);
      formData.append('target_audience', targetAudience);
      formData.append('blog_title', blogTitle);
      formData.append('blog_content', blogContent);

      const { data } = await axios.post(`${API}/social/content/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Content submitted for review!');

      // Auto-trigger AI review
      try {
        await axios.post(`${API}/social/content/${data.content_id}/ai-review`, {}, { withCredentials: true });
      } catch (e) {
        console.warn('AI review auto-trigger failed', e);
      }

      navigate('/social');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const hashtagTemplates = templates.filter(t => t.template_type === 'hashtag');
  const captionTemplates = templates.filter(t => t.template_type === 'caption');

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-4 md:p-6" data-testid="content-upload-page">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/social')} className="mb-3 text-slate-500">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold text-slate-900">Create New Post</h1>
        <p className="text-sm text-slate-500 mt-1">Upload content for AI review and admin approval</p>
      </div>

      {/* Content Type Selection */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Content Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            {[
              { id: 'image', label: 'Image Post', icon: Image },
              { id: 'video', label: 'Video Post', icon: Video },
              { id: 'blog', label: 'Blog / Article', icon: FileText },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setContentType(t.id)}
                data-testid={`type-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                  contentType === t.id
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Platform Selection */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Target Platforms</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map(p => (
              <button
                key={p.id}
                onClick={() => togglePlatform(p.id)}
                data-testid={`platform-${p.id}`}
                className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                  platforms.includes(p.id)
                    ? `${p.color} border-current shadow-sm`
                    : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* File Upload (image/video) */}
      {contentType !== 'blog' && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Upload {contentType === 'image' ? 'Image' : 'Video'}</CardTitle>
          </CardHeader>
          <CardContent>
            {!preview ? (
              <label
                className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-slate-400 transition-colors"
                data-testid="file-dropzone"
              >
                <Upload className="h-10 w-10 text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">Click to upload {contentType}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {contentType === 'image' ? 'JPEG, PNG, WebP up to 10MB' : 'MP4, WebM up to 50MB'}
                </p>
                <input
                  type="file"
                  className="hidden"
                  accept={contentType === 'image' ? 'image/jpeg,image/png,image/webp' : 'video/mp4,video/webm,video/quicktime'}
                  onChange={handleFileChange}
                  data-testid="file-input"
                />
              </label>
            ) : (
              <div className="relative">
                {contentType === 'image' ? (
                  <img src={preview} alt="Preview" className="max-h-64 rounded-lg mx-auto object-contain" />
                ) : (
                  <video src={preview} controls className="max-h-64 rounded-lg mx-auto" />
                )}
                <Button
                  variant="outline" size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => { setFile(null); setPreview(null); }}
                >
                  Remove
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Image Generator (for image/video posts) */}
      {contentType !== 'blog' && (
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <ImageGenerator
              onImageGenerated={(fileInfo) => {
                if (fileInfo) {
                  toast.success('Design imported from Canva! Ready for posting.');
                }
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Blog Content */}
      {contentType === 'blog' && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Blog Content</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Blog Title</Label>
              <Input
                value={blogTitle}
                onChange={e => setBlogTitle(e.target.value)}
                placeholder="Enter blog title..."
                data-testid="blog-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Blog Content</Label>
              <Textarea
                value={blogContent}
                onChange={e => setBlogContent(e.target.value)}
                placeholder="Write your blog post here..."
                rows={12}
                data-testid="blog-content-input"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Caption & Hashtags */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Caption & Hashtags</CardTitle>
            {(hashtagTemplates.length > 0 || captionTemplates.length > 0) && (
              <div className="flex items-center gap-2">
                <Bookmark className="h-4 w-4 text-slate-400" />
                <Select value={selectedTemplate} onValueChange={applyTemplate}>
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="template-select">
                    <SelectValue placeholder="Apply Template" />
                  </SelectTrigger>
                  <SelectContent>
                    {hashtagTemplates.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase">Hashtag Templates</div>
                        {hashtagTemplates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}
                      </>
                    )}
                    {captionTemplates.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase">Caption Templates</div>
                        {captionTemplates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Caption</Label>
            <Textarea
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="Write your post caption..."
              rows={4}
              data-testid="caption-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1"><Hash className="h-3 w-3" /> Hashtags</Label>
            <Textarea
              value={hashtags}
              onChange={e => setHashtags(e.target.value)}
              placeholder="#ShardaMotor #PowerGen #DieselGenerator..."
              rows={2}
              data-testid="hashtags-input"
            />
            <HashtagSuggest
              contentText={caption || blogContent}
              topic={blogTitle || purpose}
              platforms={platforms}
              onApply={(tags) => setHashtags(prev => prev ? `${prev} ${tags}` : tags)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Purpose & Audience */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Context</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Purpose / Reason</Label>
            <Input
              value={purpose}
              onChange={e => setPurpose(e.target.value)}
              placeholder="e.g., Product launch, Brand awareness, Event promotion..."
              data-testid="purpose-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Target Audience</Label>
            <Input
              value={targetAudience}
              onChange={e => setTargetAudience(e.target.value)}
              placeholder="e.g., B2B industrial buyers, facility managers..."
              data-testid="audience-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/social')}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-btn">
          {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          Submit for Review
        </Button>
      </div>
    </div>
  );
}
